/**
 * The typed failure of a registry-document order call.
 *
 * Kept apart from the API client, like `http.ts`, so tests that mock
 * `@/services/vnext-api` wholesale can still construct and match these.
 */

import axios from 'axios';

/**
 * How a registry-document order call failed. Placing an order is billable
 * work with three settled outcomes plus "unknown", and the API's bodies say
 * which one happened and whether an order row now exists. The generic
 * interceptor would flatten those bodies to a string (or to "Server error"
 * outside development), so the order calls bypass it and throw this.
 */
export type RegistryOrderErrorKind =
  /** 409: an open order for the same documents is on the report
   * (`orderId`, `reference`, `existingStatus`); re-order with `allow_duplicate`. */
  | 'duplicate'
  /** 422: the request itself — an unknown product code, notes too long,
   * a subject not on the report. `code` names the case when there is one. */
  | 'validation'
  /** 502 before anything was recorded: the catalogue could not be read. */
  | 'catalogue_unavailable'
  /** 502: the request never reached Armadillo; the order stays prepared
   * (or is failed once the retries are used up — `existingStatus`). */
  | 'not_sent'
  /** 502: Armadillo accepted the order without every document; it is
   * failed here and must be cancelled with Armadillo before re-ordering. */
  | 'accepted_without_documents'
  /** 502: Armadillo refused the order; it is failed. */
  | 'refused'
  /** 504 or a transport failure: sent, nothing confirmed or refused it.
   * It may be live at Armadillo — confirm, quoting the reference. */
  | 'outcome_unknown'
  /** 409: only a prepared order can be discarded or placed now. */
  | 'not_prepared'
  /** 409: the report has not been activated; place-now waits for Start. */
  | 'not_activated'
  /** 409: the order cannot be closed - it is only prepared, already
   * settled, or every document has arrived (`existingStatus` and the two
   * document counts say which). */
  | 'not_closeable'
  /** 409 on a reply: the order is only prepared, so nothing was ever sent
   * and there is nobody to answer. */
  | 'not_replyable'
  /** 503 on a reply: this environment has no send token, so replies go
   * from the mailbox. The message is the API's own sentence. */
  | 'reply_not_configured'
  /** 502 on a reply: Gmail refused or failed the send; nothing was recorded. */
  | 'send_failed'
  /** 409 on attaching or dismissing an unmatched email: someone already
   * attached or dismissed it, so the row on screen is stale. */
  | 'already_resolved'
  /** 409 on recording a quote: only an order live at Armadillo that still
   * owes documents takes one (`existingStatus` says what it is instead),
   * so the row on screen is stale. */
  | 'quote_not_allowed'
  /** 409 on starting an extraction of document information: a run is already
   * reading the order's documents, started from another tab or by someone
   * else, so the row on screen is stale. */
  | 'digest_in_progress'
  /** 409 on starting an extraction: none of the delivered files is a PDF
   * the extraction reads (trace reports are skipped). */
  | 'no_documents'
  /** 503 on starting an extraction: switched off in this environment. */
  | 'digest_disabled'
  /** 409 on adding the digest to the report: no run has completed on the
   * order, so there is nothing to add. */
  | 'digest_not_completed'
  /** 409 on adding: the report is not at a stage that takes content
   * (`existingStatus` is its `lifecycle_stage`). */
  | 'report_stage'
  /** 409 on adding: the report has no document yet. */
  | 'report_no_content'
  /** 409 on adding: the report is being edited through feedback, so its
   * document is about to move; try again when it is back in QA. */
  | 'report_busy'
  /** 409 on adding: the report's document moved to a newer version since
   * this screen was read (`detail.version`), so the screen is stale. */
  | 'content_changed'
  /** 409 on adding: one marker of the block added earlier is missing or
   * the pair is inverted; the message says how to repair the file. */
  | 'markers_damaged'
  | 'not_found'
  | 'other';

/** Which endpoint answered: a reply's 502 and 503 mean something other
 * than an order call's, an attach's 409 names the email or the order, and
 * a quote's 409 is the order's state, and a digest call's 409 and 503 are
 * its own refusals. */
export type RegistryOrderCall = 'order' | 'reply' | 'attach' | 'quote' | 'digest';

export class RegistryOrderError extends Error {
  readonly kind: RegistryOrderErrorKind;
  readonly status: number | null;
  readonly detail: Record<string, unknown> | null;
  /** An order row exists (or may) after this failure — re-read the list
   * rather than treating the screen as unchanged. */
  readonly orderMayExist: boolean;

  constructor(
    kind: RegistryOrderErrorKind,
    message: string,
    options: {
      status: number | null;
      detail?: Record<string, unknown> | null;
      orderMayExist: boolean;
    }
  ) {
    super(message);
    this.name = 'RegistryOrderError';
    this.kind = kind;
    this.status = options.status;
    this.detail = options.detail ?? null;
    this.orderMayExist = options.orderMayExist;
  }

  private detailString(key: string): string | null {
    const value = this.detail?.[key];
    return typeof value === 'string' && value ? value : null;
  }

  /** The API's machine code for a validation failure (e.g. `PRODUCT_NOT_IN_CATALOGUE`). */
  get code(): string | null {
    return this.detailString('code');
  }

  /** The order the failure is about: the duplicate found, or the row the
   * send left behind. */
  get orderId(): string | null {
    return this.detailString('order_id');
  }

  get reference(): string | null {
    return this.detailString('reference');
  }

  /** That order's status as the API reported it; on a report-stage
   * refusal, the report's lifecycle stage. */
  get existingStatus(): string | null {
    return this.detailString('status') ?? this.detailString('lifecycle_stage');
  }
}

const CATALOGUE_UNAVAILABLE_PREFIX = 'Could not read the document catalogue';

/** One entry of pydantic's error list as "field: message" — "Field
 * required" alone says nothing about what to change. */
function fieldMessage(entry: unknown): string | null {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const { msg, loc } = entry as { msg?: unknown; loc?: unknown };
  if (typeof msg !== 'string') {
    return null;
  }
  const field = Array.isArray(loc)
    ? loc.filter((part) => typeof part === 'string' && part !== 'body').pop()
    : null;
  return typeof field === 'string' ? `${field}: ${msg}` : msg;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/** Pull the human message out of a FastAPI `detail`, whatever its shape. */
export function detailMessage(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') {
    return detail || fallback;
  }
  if (Array.isArray(detail)) {
    const messages = detail.map(fieldMessage).filter((msg): msg is string => msg !== null);
    return messages.length ? messages.join('; ') : fallback;
  }
  const message = isRecord(detail) ? detail.message : null;
  return typeof message === 'string' && message ? message : fallback;
}

/** What a failure means: its kind, and whether an order row may exist. */
type Outcome = readonly [kind: RegistryOrderErrorKind, orderMayExist: boolean];

const PLAIN_OUTCOMES: Partial<Record<number, Outcome>> = {
  404: ['not_found', false],
  422: ['validation', false],
  504: ['outcome_unknown', true],
};

function conflictOutcome(detail: Record<string, unknown> | null): Outcome {
  if (typeof detail?.order_id === 'string') {
    return ['duplicate', true];
  }
  // A refused close names the document counts that decided it.
  if (detail && 'expected_document_count' in detail) {
    return ['not_closeable', true];
  }
  return [detail && 'lifecycle_stage' in detail ? 'not_activated' : 'not_prepared', true];
}

/**
 * A 502 is four different things. With an `outcome` it is the API saying
 * what the send left behind. As a plain message it is either the catalogue
 * read failing before anything was recorded, or Armadillo refusing the
 * order. Without a FastAPI `detail` at all it did not come from the API —
 * an edge proxy answered while the call was in flight — so nothing is
 * known about the order.
 */
function badGatewayOutcome(detail: unknown): Outcome {
  const outcome = isRecord(detail) ? detail.outcome : null;
  if (outcome === 'not_sent' || outcome === 'accepted_without_documents') {
    return [outcome, true];
  }
  if (typeof detail !== 'string') {
    return ['outcome_unknown', true];
  }
  return detail.startsWith(CATALOGUE_UNAVAILABLE_PREFIX)
    ? ['catalogue_unavailable', false]
    : ['refused', true];
}

/** A reply never changes the order unless it is recorded, so none of its
 * failures leaves the screen stale: a 409 names the order as it is, a 502
 * recorded nothing, a 503 was refused before anything was tried. */
const REPLY_OUTCOMES: Partial<Record<number, Outcome>> = {
  409: ['not_replyable', false],
  502: ['send_failed', false],
  503: ['reply_not_configured', false],
};

/** The code a 409 on an unmatched email carries: the API sends it as the
 * `detail` string itself; a body that wraps it under `code` reads the same. */
function conflictCode(detail: unknown): string | null {
  if (typeof detail === 'string') {
    return detail;
  }
  const code = isRecord(detail) ? detail.code : null;
  return typeof code === 'string' ? code : null;
}

/**
 * Attaching an unmatched email to an order, or dismissing it, changes the
 * email's row and nothing else. A 409 is one of two refusals: the email
 * was already dealt with by someone else (`already_resolved`, so the list
 * on screen is stale), or the chosen order was never sent
 * (`order_not_placed`: nothing was changed, pick another). The rest read
 * as for an order call.
 */
function attachOutcome(status: number, detail: unknown): Outcome {
  if (status === 409) {
    return conflictCode(detail) === 'already_resolved'
      ? ['already_resolved', true]
      : ['not_prepared', false];
  }
  return PLAIN_OUTCOMES[status] ?? ['other', status >= 500];
}

/** The 409 refusals of a digest start and of an add to the report, by the
 * code the API sends. */
const DIGEST_CONFLICTS: Readonly<Record<string, Outcome>> = {
  // Another run is reading the documents: the row on screen is stale.
  digest_in_progress: ['digest_in_progress', true],
  // Nothing to read: the order is as it was.
  no_documents: ['no_documents', false],
  // Adding to the report. The order or its report moved since the screen
  // was read, except where the report's document is simply missing.
  digest_not_completed: ['digest_not_completed', true],
  report_stage: ['report_stage', true],
  report_no_content: ['report_no_content', false],
  report_busy: ['report_busy', true],
  content_changed: ['content_changed', true],
  markers_damaged: ['markers_damaged', true],
};

/**
 * Starting an extraction of document information changes nothing but the
 * order's digest record, and reading it changes nothing at all; adding
 * the digest to the report changes the report's document, or nothing. A
 * 409 is read by its code: another run is already in progress, no
 * delivered file can be read, or one of the add's refusals. A 503 means
 * the extraction is switched off in this environment. The rest read as
 * for an order call.
 */
function digestOutcome(status: number, detail: unknown): Outcome {
  if (status === 409) {
    return DIGEST_CONFLICTS[conflictCode(detail) ?? ''] ?? ['other', true];
  }
  if (status === 503) {
    return ['digest_disabled', false];
  }
  return PLAIN_OUTCOMES[status] ?? ['other', status >= 500];
}

/**
 * Recording the desk's quote on its own changes the order or nothing. A
 * 409 is the order's state refusing it (never sent, unconfirmed, settled,
 * or nothing owed any more), so the row on screen is stale; a 422 is the
 * request (`code`: `expected_at_set`, `unknown_message`, or a field) and
 * changed nothing. The rest read as for an order call.
 */
function quoteOutcome(status: number): Outcome {
  if (status === 409) {
    return ['quote_not_allowed', true];
  }
  return PLAIN_OUTCOMES[status] ?? ['other', status >= 500];
}

function outcomeFor(status: number, detail: unknown, call: RegistryOrderCall): Outcome {
  if (call === 'attach') {
    return attachOutcome(status, detail);
  }
  if (call === 'digest') {
    return digestOutcome(status, detail);
  }
  if (call === 'quote') {
    return quoteOutcome(status);
  }
  const replyOutcome = call === 'reply' ? REPLY_OUTCOMES[status] : undefined;
  if (replyOutcome) {
    return replyOutcome;
  }
  if (status === 409) {
    return conflictOutcome(isRecord(detail) ? detail : null);
  }
  if (status === 502) {
    return badGatewayOutcome(detail);
  }
  return PLAIN_OUTCOMES[status] ?? ['other', status >= 500];
}

/** Map a non-2xx answer from an order endpoint to its typed error. `call`
 * says which endpoint answered: a reply's 502 and 503 are its own kinds,
 * an attach's 409 is read by its code, and a quote's 409 is its own kind. */
export function registryOrderErrorFor(
  status: number,
  body: unknown,
  call: RegistryOrderCall = 'order'
): RegistryOrderError {
  const detail: unknown = isRecord(body) ? body.detail : undefined;
  const [kind, orderMayExist] = outcomeFor(status, detail, call);
  return new RegistryOrderError(kind, detailMessage(detail, `Error ${status}`), {
    status,
    detail: isRecord(detail) ? detail : null,
    orderMayExist,
  });
}

/** A transport failure (timeout, dropped connection) while an order call
 * was in flight: the request may have reached the API, so the outcome is
 * unknown until the list is re-read. */
export function registryOrderTransportError(err: unknown): RegistryOrderError {
  const message =
    axios.isAxiosError(err) && err.code === 'ECONNABORTED'
      ? 'The request timed out before Armadillo answered. The order may have been placed — check the list before re-ordering.'
      : err instanceof Error
        ? err.message
        : 'The request failed';
  return new RegistryOrderError('outcome_unknown', message, { status: null, orderMayExist: true });
}
